import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface SubscriptionInfo {
  plan: string;
  status: string;
  trialEndsAt: Date | null;
  trialStartedAt: Date | null;
  billingCycleStart: Date | null;
  splitsThisMonth: number;
  daysRemaining: number | null;
  isTrialExpired: boolean;
  isActive: boolean;
  canCreateSplit: boolean;
  splitLimit: number | null;
}

export function useSubscription() {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [merchantId, setMerchantId] = useState<string | null>(null);
  const lastUserId = useRef<string | null>(null);

  useEffect(() => {
    // Don't do anything while auth is still loading
    if (authLoading) {
      setLoading(true);
      return;
    }

    // If no user, reset state and stop loading
    if (!user) {
      setSubscription(null);
      setMerchantId(null);
      setLoading(false);
      lastUserId.current = null;
      return;
    }

    // Skip if we already fetched for this user
    if (lastUserId.current === user.id) {
      return;
    }

    const fetchSubscription = async () => {
      setLoading(true);
      lastUserId.current = user.id;

      try {
        // Get merchant association
        const { data: merchantUser, error: muError } = await supabase
          .from('merchant_users')
          .select('merchant_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (muError || !merchantUser) {
          console.log('No merchant association found for subscription');
          setLoading(false);
          return;
        }

        setMerchantId(merchantUser.merchant_id);

        // Get merchant details
        const { data: merchant, error: mError } = await supabase
          .from('merchants')
          .select('plan, subscription_status, trial_ends_at, trial_started_at, billing_cycle_start, splits_this_month')
          .eq('id', merchantUser.merchant_id)
          .single();

        if (mError || !merchant) {
          console.log('No merchant found for subscription');
          setLoading(false);
          return;
        }

        const now = new Date();
        const trialEndsAt = merchant.trial_ends_at ? new Date(merchant.trial_ends_at) : null;
        const trialStartedAt = merchant.trial_started_at ? new Date(merchant.trial_started_at) : null;
        const billingCycleStart = merchant.billing_cycle_start ? new Date(merchant.billing_cycle_start) : null;

        // Calculate days remaining in trial
        let daysRemaining: number | null = null;
        let isTrialExpired = false;

        if (trialEndsAt) {
          const diff = trialEndsAt.getTime() - now.getTime();
          daysRemaining = Math.ceil(diff / (1000 * 60 * 60 * 24));
          isTrialExpired = daysRemaining < 0;
        }

        // Determine if subscription is active
        const isActive = 
          merchant.subscription_status === 'active' || 
          (merchant.subscription_status === 'trialing' && !isTrialExpired);

        // Get plan limits
        const planLimits: Record<string, number | null> = {
          trial: 10,
          starter: 50,
          growth: null,
          enterprise: null,
        };

        const splitLimit = planLimits[merchant.plan] ?? null;
        const canCreateSplit = splitLimit === null || merchant.splits_this_month < splitLimit;

        setSubscription({
          plan: merchant.plan,
          status: merchant.subscription_status,
          trialEndsAt,
          trialStartedAt,
          billingCycleStart,
          splitsThisMonth: merchant.splits_this_month,
          daysRemaining: daysRemaining !== null && daysRemaining > 0 ? daysRemaining : null,
          isTrialExpired,
          isActive,
          canCreateSplit,
          splitLimit,
        });
      } catch (error) {
        console.error("Error fetching subscription:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user, authLoading]);

  return { subscription, loading, merchantId };
};
